// Minimal markdown → HTML converter for the live preview in the resume
// workshop. Mirrors apps/worker/src/lib/markdown.ts (same supported syntax)
// but lives in the web package so the bundle doesn't import worker code.
//
// Wraps the body in a <div class="prose-resume"> so the global Tailwind
// utilities can paint it. We HTML-escape every leaf and only re-introduce
// the supported syntax — never injects raw user HTML.

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch] ?? ch);
}

function inline(s: string): string {
  let out = escapeHtml(s);
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+|mailto:[^\s)]+)\)/g,
    (_m, text: string, url: string) =>
      `<a class="text-accent-300 hover:text-accent-200" href="${url}" rel="noopener noreferrer" target="_blank">${text}</a>`,
  );
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/(?<!\*)\*(?!\*)([^*\n]+)\*(?!\*)/g, "<em>$1</em>");
  out = out.replace(/`([^`]+)`/g, "<code class=\"rounded bg-neutral-800 px-1\">$1</code>");
  return out;
}

export function renderMarkdownPreview(md: string): string {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();
    if (!trimmed) {
      i += 1;
      continue;
    }
    if (/^(?:-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
      out.push('<hr class="my-3 border-neutral-800" />');
      i += 1;
      continue;
    }
    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = heading[1]!.length;
      const text = heading[2]!.trim();
      const cls =
        level === 1
          ? "mt-1 mb-2 text-xl font-bold text-neutral-50 border-b border-neutral-700 pb-1"
          : level === 2
            ? "mt-4 mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-300 border-b border-neutral-800 pb-0.5"
            : "mt-3 mb-1 text-sm font-semibold text-neutral-100";
      out.push(`<h${level} class="${cls}">${inline(text)}</h${level}>`);
      i += 1;
      continue;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const t = lines[i]!.trim();
        if (!/^[-*]\s+/.test(t)) break;
        items.push(t.replace(/^[-*]\s+/, ""));
        i += 1;
      }
      out.push('<ul class="mb-2 list-disc space-y-1 pl-5">');
      for (const item of items) {
        out.push(`<li>${inline(item)}</li>`);
      }
      out.push("</ul>");
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const t = lines[i]!;
      const tt = t.trim();
      if (!tt) break;
      if (/^(?:#{1,3}\s+|[-*]\s+|-{3,}$|_{3,}$|\*{3,}$)/.test(tt)) break;
      para.push(tt);
      i += 1;
    }
    if (para.length > 0) {
      out.push(`<p class="mb-2">${inline(para.join(" "))}</p>`);
    }
  }
  return out.join("\n");
}
